import { FC } from 'react';
import { Bot, User } from 'lucide-react';

interface TranscriptConversationProps {
  transcript: string;
  contactFirstName?: string;
  assistantName?: string;
}

interface Message {
  speaker: string;
  text: string;
}

function isAISpeaker(speaker: string): boolean {
  const s = speaker.toLowerCase();
  return s.includes('agent') || s.includes('assistant') || s === 'ai' || s === 'system';
}

function parseTranscript(transcript: string): Message[] {
  if (!transcript) return [];

  // Try JSON array format: [{"user": "agent"|"user", "text": "..."}, ...]
  // Bland AI's `transcripts` field uses this format
  if (transcript.trimStart().startsWith('[')) {
    try {
      const parsed = JSON.parse(transcript) as Array<{ user?: string; role?: string; text?: string; message?: string }>;
      if (Array.isArray(parsed)) {
        return parsed
          .map((item) => ({
            speaker: (item.user ?? item.role ?? 'user').trim(),
            text: (item.text ?? item.message ?? '').trim(),
          }))
          .filter((m) => m.text);
      }
    } catch {
      // fall through to plain text parsing
    }
  }

  // Plain text format: "speaker: text\nspeaker: text"
  return transcript
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line.length > 0)
    .map((line) => {
      const colonIdx = line.indexOf(':');
      if (colonIdx === -1) return { speaker: 'user', text: line };
      return {
        speaker: line.slice(0, colonIdx).trim(),
        text: line.slice(colonIdx + 1).trim(),
      };
    })
    .filter((m) => m.text);
}

export const TranscriptConversation: FC<TranscriptConversationProps> = ({
  transcript,
  contactFirstName,
  assistantName,
}) => {
  if (!transcript) return null;

  const messages = parseTranscript(transcript);
  if (messages.length === 0) return null;

  return (
    <div className="flex flex-col gap-2.5">
      {messages.map((message, index) => {
        const isAI = isAISpeaker(message.speaker);

        return (
          <div key={index} className={`flex items-start gap-2 ${isAI ? 'flex-row-reverse' : ''}`}>
            <div
              className={`
                flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center
                ${isAI ? 'btn-ai-gradient' : 'bg-zinc-700 dark:bg-zinc-200'}
              `}
            >
              {isAI ? (
                <Bot className="w-3 h-3 text-white dark:text-zinc-900" />
              ) : (
                <User className="w-3 h-3 text-white dark:text-zinc-900" />
              )}
            </div>

            <div
              className={`
                max-w-[85%] rounded-lg px-2.5 py-2
                ${isAI ? 'bg-zinc-800 dark:bg-zinc-700 text-zinc-100' : 'bg-gray-100 dark:bg-zinc-800 text-gray-700 dark:text-zinc-100'}
              `}
            >
              <div className="t-label font-semibold text-inherit opacity-70 mb-0.5">
                {isAI
                  ? assistantName
                    ? `${assistantName.charAt(0).toUpperCase() + assistantName.slice(1)} (${message.speaker})`
                    : 'Assistant'
                  : contactFirstName
                    ? contactFirstName.charAt(0).toUpperCase() + contactFirstName.slice(1)
                    : 'User'}
              </div>
              <div className="t-body text-inherit whitespace-pre-wrap">{message.text}</div>
            </div>
          </div>
        );
      })}
    </div>
  );
};
